import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { useLlmFetch } from "hooks/useLlmFetch";

export interface Subsection {
  subsectionId: string;
  subsectionLabel: string;
  fields: string[];
}

interface SpeechToTextContextData {
  isListening: boolean;
  transcript: string;
  isProcessing: boolean;
  parsedFields: Record<string, string>[] | null;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
  processWithLLM: (transcriptText: string) => void;
  cancelAndReset: () => void;
  retryProcessing: () => void;
  isSupported: boolean;
  setCodesList: React.Dispatch<React.SetStateAction<string[]>>;
}

const SpeechToTextContext = createContext<SpeechToTextContextData | undefined>(
  undefined
);

export const SpeechToTextProvider = ({ children }: { children: ReactNode }) => {
  const [transcript, setTranscript] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedFields, setParsedFields] = useState<
    SpeechToTextContextData["parsedFields"] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [codesList, setCodesList] = useState<string[]>([]);
  const llmFetch = useLlmFetch();

  const {
    transcript: liveTranscript,
    resetTranscript,
    listening,
  } = useSpeechRecognition();

  useEffect(() => {
    if (listening) {
      setTranscript(liveTranscript);
    }
  }, [liveTranscript, listening]);

  const startListening = useCallback(async () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
      setError("Speech recognition is not supported in this browser");
      return;
    }

    setTranscript("");
    setError(null);
    setParsedFields(null);
    resetTranscript();

    try {
      await SpeechRecognition.startListening({
        continuous: true,
        language: "en-US",
      });
    } catch (err) {
      setError("Failed to start speech recognition");
      // eslint-disable-next-line no-console
      console.error("Speech recognition error:", err);
    }
  }, [resetTranscript]);

  const processWithLLM = useCallback(
    async (transcriptText: string) => {
      if (!transcriptText.trim()) {
        setError("No transcript to process");
        return;
      }

      setIsProcessing(true);
      setError(null);

      try {
        const response = await llmFetch("/chart/match-fast", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            chart_structure: codesList,
            tts_text: transcriptText,
          }),
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          setParsedFields(
            Object.entries(data.data as Record<string, string>).map(
              ([rawKey, value]) => {
                const key = rawKey.split(":")[0];
                return { key, value };
              }
            )
          );
        } else {
          throw new Error(data.error || "Failed to parse transcript");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        setError(errorMessage);
        // eslint-disable-next-line no-console
        console.error("LLM parsing failed:", err);
      } finally {
        setIsProcessing(false);
      }
    },
    [codesList, llmFetch]
  );

  const stopListening = useCallback(() => {
    SpeechRecognition.stopListening();
  }, []);

  const cancelAndReset = useCallback(() => {
    if (listening) {
      SpeechRecognition.stopListening();
    }
    setTranscript("");
    setParsedFields(null);
    setError(null);
    resetTranscript();
  }, [listening, resetTranscript]);

  const retryProcessing = useCallback(() => {
    if (transcript.trim()) {
      processWithLLM(transcript);
    }
  }, [transcript, processWithLLM]);

  const value = {
    isListening: listening,
    transcript,
    isProcessing,
    parsedFields,
    error,
    startListening,
    stopListening,
    processWithLLM,
    cancelAndReset,
    retryProcessing,
    isSupported: SpeechRecognition.browserSupportsSpeechRecognition(),
    setCodesList,
  };

  return (
    <SpeechToTextContext.Provider value={value}>
      {children}
    </SpeechToTextContext.Provider>
  );
};

export const useSpeechToTextContext = (): SpeechToTextContextData => {
  const context = useContext(SpeechToTextContext);
  if (context === undefined) {
    throw new Error(
      "useSpeechToTextContext must be used within a SpeechToTextProvider"
    );
  }
  return context;
};
